import { supabase } from './client';
import { getCurrentUser } from './client';

// ============================================================
// STORAGE BUCKET CONSTANTS
// ============================================================

const LESSON_COVERS_BUCKET = 'make-9d7d881a-lesson-covers';

// ============================================================
// INITIALIZATION
// ============================================================

/**
 * Initialize storage buckets (create if they don't exist)
 * This should be called when the app initializes
 */
export async function initializeStorageBuckets() {
  try {
    // Note: listBuckets and createBucket require service role
    // If this fails, the bucket may need to be created manually in Supabase dashboard
    const { data: buckets, error: listError } = await supabase.storage.listBuckets();

    if (listError) {
      console.warn('Warning: Could not list buckets (may require service role):', listError);
      return;
    }

    const bucketExists = buckets?.some(bucket => bucket.name === LESSON_COVERS_BUCKET);

    if (!bucketExists) {
      console.log(`Creating storage bucket: ${LESSON_COVERS_BUCKET}`);
      const { error: createError } = await supabase.storage.createBucket(LESSON_COVERS_BUCKET, {
        public: true, // Make bucket public so images can be accessed
        fileSizeLimit: 5242880 // 5MB limit
      });

      if (createError) {
        console.warn(`Warning creating bucket (may already exist): ${createError?.message || JSON.stringify(createError)}`);
      } else {
        console.log(`Storage bucket created: ${LESSON_COVERS_BUCKET}`);
      }
    } else {
      console.log(`Storage bucket already exists: ${LESSON_COVERS_BUCKET}`);
    }
  } catch (error) {
    console.warn('Warning initializing storage buckets:', error);
    // Don't throw - uploads might still work if bucket exists
  }
}

// ============================================================
// UPLOAD FUNCTIONS
// ============================================================

/**
 * Upload a lesson cover image
 * @param file - The image file to upload
 * @param lessonId - Optional lesson ID (generates random name if not provided)
 * @returns The public URL of the uploaded image
 */
export async function uploadLessonCover(file: File, lessonId?: string): Promise<string> {
  try {
    const user = await getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    // Generate unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = lessonId 
      ? `${lessonId}.${fileExt}`
      : `${user.id}-${Date.now()}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    // Upload file
    const { data, error } = await supabase.storage
      .from(LESSON_COVERS_BUCKET)
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Allow replacing existing files
      });

    if (error) throw error;

    // Get public URL
    const { data: { publicUrl } } = supabase.storage
      .from(LESSON_COVERS_BUCKET)
      .getPublicUrl(filePath);

    return publicUrl;
  } catch (error) {
    console.error('Error uploading lesson cover:', error);
    throw error;
  }
}

/**
 * Delete a lesson cover image
 * @param imageUrl - The URL of the image to delete
 */
export async function deleteLessonCover(imageUrl: string): Promise<void> {
  try {
    // Extract file path from URL
    const url = new URL(imageUrl);
    const pathParts = url.pathname.split(`${LESSON_COVERS_BUCKET}/`);
    if (pathParts.length < 2) return;
    
    const filePath = pathParts[1];

    const { error } = await supabase.storage
      .from(LESSON_COVERS_BUCKET)
      .remove([filePath]);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting lesson cover:', error);
    throw error;
  }
}

/**
 * Validate image file
 * @param file - File to validate
 * @returns Error message if invalid, null if valid
 */
export function validateImageFile(file: File): string | null {
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return 'Please upload a valid image file (JPG, PNG, or WEBP)';
  }

  // Check file size (5MB max)
  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return 'Image size must be less than 5MB';
  }

  return null;
}